<template>
  <SideBarLayoutContent full-width sheet-below-md :side-bar-title="t('lumia.filters.title')">
    <template v-slot="{ toggleSheet, showSheet }">
      <div class="py-4">
        <h1 class="text-display-small grow">{{ $t('lumia.lesson.pageTitle') }}</h1>
      </div>
      <LoadingWrapper :state="runningLessonsStatus">
        <BaseAnimatedExtend :is-extended="runningLessons.length > 0 && !hasFilter && !showSheet">
          <section class="mb-6 flex flex-col gap-1">
            <h2 class="text-title-medium text-on-surface-variant">
              {{ t('lumia.lesson.myCountRunning', { count: runningLessons.length }) }}
            </h2>
            <ul class="grid grid-cols-1 gap-0.5">
              <li
                v-for="lesson in runningLessons"
                :key="lesson.id"
                class="bg-surface-container-lowest first:rounded-t-large last:rounded-b-large rounded-extra-small overflow-hidden"
              >
                <LessonListItem :lesson="lesson" />
              </li>
            </ul>
          </section>
        </BaseAnimatedExtend>
      </LoadingWrapper>

      <LoadingWrapper :state="status">
        <BaseAnimatedExtend
          :is-extended="bookmarkedImpulses.length > 0 && !hasFilter && !showSheet"
        >
          <section v-if="bookmarkedImpulses.length && !showSheet" class="mb-6 flex flex-col gap-2">
            <h2 class="text-title-medium text-on-surface-variant">
              {{ t('lumia.lesson.bookmarkedImpulses', { count: bookmarkedImpulses.length }) }}
            </h2>
            <BaseHorizontalOverflow class="w-full" show-buttons>
              <ul class="flex w-max flex-row gap-4">
                <li v-for="impulse in bookmarkedImpulses" :key="impulse.id" class="w-sm shrink-0">
                  <LessonImpulseListItemCompact :impulse="impulse" class="h-full w-full" />
                </li>
              </ul>
            </BaseHorizontalOverflow>
          </section>
        </BaseAnimatedExtend>
        <section>
          <div class="flex flex-row items-center py-1">
            <h1 class="text-headline-small grow">{{ $t('lumia.pageTitle') }}</h1>
            <div class="relative md:hidden">
              <BaseTextButton
                color="outlined"
                icon="tune"
                toggle
                :selected="showSheet"
                @click="toggleSheet()"
                >{{ $t('lumia.filters.title') }}</BaseTextButton
              >
              <div
                v-if="hasFilter"
                class="bg-tertiary absolute -top-1 -right-1 h-2 w-2 rounded-full"
              ></div>
            </div>
          </div>
          <div class="mb-4 hidden md:block">
            <LessonImpulseLibraryFilters
              v-model:selected-kompetenz-ids="filters.kompetenzIds"
              inline
              :kompetenzen="options.kompetenzen"
              :grades="options.grades"
              :subjects="options.subjects"
              :selected-grades="filters.grades"
              :selected-subject-codes="filters.subjectCodes"
              :bookmarked-only="filters.bookmarkedOnly"
              @toggle-kompetenz="toggleKompetenz"
              @toggle-grade="toggleGrade"
              @toggle-subject="toggleSubject"
              @toggle-bookmarked="toggleBookmarked"
              @reset="resetFilters"
            />
          </div>
          <div class="mb-4 flex flex-row">
            <div class="mr-4 flex h-8 flex-row items-center">
              <span class="text-title-medium text-on-surface-variant whitespace-nowrap"
                >{{ impulseList.length }} Impulse</span
              >
            </div>

            <div class="flex flex-row flex-wrap gap-1">
              <BaseChip
                v-for="kompetenz in selectedKompetenzen"
                :key="kompetenz.id"
                type="input"
                :color="colorForUeberfachlicheKompetenz(kompetenz)"
                @click="toggleKompetenz(kompetenz.id)"
                >{{ kompetenz.title }}</BaseChip
              >
              <BaseChip
                v-for="grade in filters.grades"
                :key="grade"
                type="input"
                @click="toggleGrade(grade)"
                >{{ $t('lumia.gradeLabels', { classes: `${grade}.` }) }}</BaseChip
              >
              <BaseChip
                v-for="subject in filters.subjectCodes"
                :key="subject"
                type="input"
                @click="toggleSubject(subject)"
                >{{ subject }}</BaseChip
              >
              <BaseChip v-if="filters.bookmarkedOnly" type="input" @click="toggleBookmarked()">{{
                $t('lumia.tabs.bookmarks')
              }}</BaseChip>
            </div>
            <div v-if="hasFilter" class="ml-1 hidden md:block">
              <BaseTextButton
                color="text"
                icon="filter_alt_off"
                size="XS"
                class="text-on-surface-variant!"
                @click="resetFilters"
                >{{ t('lumia.filters.reset') }}</BaseTextButton
              >
            </div>
          </div>
          <ul
            v-if="impulseList.length"
            class="@8xl/content:grid-cols-4 grid grid-cols-1 gap-6 pb-6 @xl/content:grid-cols-2 @5xl/content:grid-cols-3"
          >
            <li v-for="impulse in impulseList" :key="impulse.id" class="h-full">
              <LessonImpulseListItem :impulse="impulse" class="h-full" />
            </li>
          </ul>
          <EmptyPage
            v-else
            :title="t('lumia.filters.empty.title')"
            :description="t('lumia.filters.empty.description')"
            icon="tune"
          >
            <BaseTextButton color="tonal" icon="filter_alt_off" size="M" @click="resetFilters">{{
              t('lumia.filters.reset')
            }}</BaseTextButton>
          </EmptyPage>
        </section>
      </LoadingWrapper>
    </template>
    <template #side-bar>
      <LessonImpulseLibraryFilters
        :kompetenzen="options.kompetenzen"
        :grades="options.grades"
        :subjects="options.subjects"
        :selected-kompetenz-ids="filters.kompetenzIds"
        :selected-grades="filters.grades"
        :selected-subject-codes="filters.subjectCodes"
        :bookmarked-only="filters.bookmarkedOnly"
        @toggle-kompetenz="toggleKompetenz"
        @toggle-grade="toggleGrade"
        @toggle-subject="toggleSubject"
        @toggle-bookmarked="toggleBookmarked"
        @reset="resetFilters"
      />
    </template>
  </SideBarLayoutContent>
</template>
<script lang="ts" setup>
  import { computed } from 'vue'
  import { useI18n } from 'vue-i18n'
  import BaseTextButton from '@/core/components/base/button/text-button/BaseTextButton.vue'
  import EmptyPage from '@/core/components/empty-states/EmptyPage.vue'
  import SideBarLayoutContent from '@/core/components/layouts/SideBarLayoutContent.vue'
  import LoadingWrapper from '@/core/components/loading/LoadingWrapper.vue'
  import { useLessonImpulses } from '../composables/use-lesson-impulses'
  import { useLessonImpulseFilters } from '../composables/use-lesson-impulse-filters'
  import LessonImpulseListItem from '../components/LessonImpulseListItem.vue'
  import LessonImpulseLibraryFilters from '../components/LessonImpulseLibraryFilters.vue'
  import BaseHorizontalOverflow from '@/core/components/base/horizontal-overflow/BaseHorizontalOverflow.vue'
  import LessonImpulseListItemCompact from '../components/LessonImpulseListItemCompact.vue'
  import { useLessons } from '../composables/use-lessons'
  import LessonListItem from '../components/LessonListItem.vue'
  import BaseAnimatedExtend from '@/core/components/base/animated-extend/BaseAnimatedExtend.vue'
  import BaseChip from '@/core/components/base/chip/BaseChip.vue'
  import { colorForUeberfachlicheKompetenz } from '../models/ueberfachliche-kompetenz-color.ts'

  const { t } = useI18n()
  const { impulses, status, bookmarkedImpulses } = useLessonImpulses()
  const { lessons, status: runningLessonsStatus } = useLessons({ state: 'running' })
  const runningLessons = computed(() => lessons.value ?? [])
  const {
    filters,
    options,
    hasFilter,
    toggleKompetenz,
    toggleGrade,
    toggleSubject,
    toggleBookmarked,
    resetFilters,
    applyFilters,
  } = useLessonImpulseFilters(impulses)

  const impulseList = computed(() => applyFilters(impulses.value))

  const selectedKompetenzen = computed(() =>
    options.value.kompetenzen.filter((kompetenz) => filters.kompetenzIds.includes(kompetenz.id)),
  )
</script>
